import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";

type DefaultColorState = boolean | string;
type DefaultValueState = boolean | string | number | Record<string, unknown> | unknown[];

/**
 * FeatureDefaultsStateService
 * --------------------------------------------------------------------
 * Holds the per-feature "default value" and "default color" streams
 * previously declared inline on `ApiService`.
 *
 * NOTE on typing: the legacy `ApiService` exposed every BehaviorSubject
 * without concrete payload contracts. Many consuming components pass booleans
 * where strings are stored (and vice-versa) — this is intentional
 * "tri-state" semantics in the existing codebase (`""`, `true`, or a
 * css class name). The unions below preserve that contract while removing
 * the raw untyped usage. A follow-up pass can tighten
 * each stream one feature at a time.
 */
@Injectable({ providedIn: "root" })
export class FeatureDefaultsStateService {
  // --- Default color streams ----------------------------------------
  readonly aloDefaultColor = new BehaviorSubject<DefaultColorState>("");
  readonly getAloDefaultColor: Observable<DefaultColorState> = this.aloDefaultColor.asObservable();

  readonly startupDefaultColor = new BehaviorSubject<DefaultColorState>("");
  readonly getStartupDefaultColor: Observable<DefaultColorState> = this.startupDefaultColor.asObservable();

  readonly aocDefaultColor = new BehaviorSubject<DefaultColorState>("");
  readonly getAocDefaultColor: Observable<DefaultColorState> = this.aocDefaultColor.asObservable();

  readonly srpsuDefaultColor = new BehaviorSubject<DefaultColorState>("");
  readonly getSrpsuDefaultColor: Observable<DefaultColorState> = this.srpsuDefaultColor.asObservable();

  readonly cloDefaultColor = new BehaviorSubject<DefaultColorState>("");
  readonly getCloDefaultColor: Observable<DefaultColorState> = this.cloDefaultColor.asObservable();

  readonly cloLiteDefaultColor = new BehaviorSubject<DefaultColorState>("");
  readonly getCLOliteDefaultColor: Observable<DefaultColorState> = this.cloLiteDefaultColor.asObservable();

  readonly dynaDefaultColor = new BehaviorSubject<DefaultColorState>("");
  readonly getDynaDefaultColor: Observable<DefaultColorState> = this.dynaDefaultColor.asObservable();

  readonly ampdimDefaultColor = new BehaviorSubject<DefaultColorState>("");
  readonly getAmpDimDefaultColor: Observable<DefaultColorState> = this.ampdimDefaultColor.asObservable();

  readonly luminDefaultColor = new BehaviorSubject<DefaultColorState>("");
  readonly getLuminDefaultColor: Observable<DefaultColorState> = this.luminDefaultColor.asObservable();

  readonly dcemergencyDefaultColor = new BehaviorSubject<DefaultColorState>("");
  readonly getDcemergencyDefaultColor: Observable<DefaultColorState> =
    this.dcemergencyDefaultColor.asObservable();

  readonly touchDimDefaultColor = new BehaviorSubject<DefaultColorState>("");
  readonly getTouchDimDefaultColor: Observable<DefaultColorState> = this.touchDimDefaultColor.asObservable();

  readonly owpDefaultColor = new BehaviorSubject<DefaultColorState>("");
  readonly getowpDefaultColor: Observable<DefaultColorState> = this.owpDefaultColor.asObservable();

  readonly diia253maintenanceDefaultColor = new BehaviorSubject<DefaultColorState>("");
  readonly getdiia253maintenanceDefaultColor: Observable<DefaultColorState> =
    this.diia253maintenanceDefaultColor.asObservable();

  readonly minDimLevelDefaultColor = new BehaviorSubject<DefaultColorState>("");
  readonly getminDimLevelDefaultColor: Observable<DefaultColorState> =
    this.minDimLevelDefaultColor.asObservable();

  readonly FlextuneDefaultColor = new BehaviorSubject<DefaultColorState>("");
  readonly getflextuneDefaultColor: Observable<DefaultColorState> = this.FlextuneDefaultColor.asObservable();

  readonly DtlDefaultColor = new BehaviorSubject<DefaultColorState>("");
  readonly getdtlDefaultColor: Observable<DefaultColorState> = this.DtlDefaultColor.asObservable();

  readonly dscDefaultColor = new BehaviorSubject<DefaultColorState>("");
  readonly getDscDefaultColor: Observable<DefaultColorState> = this.dscDefaultColor.asObservable();

  readonly lowmainslockDefaultColor = new BehaviorSubject<DefaultColorState>("");
  readonly getLowMainsLockDefaultColor: Observable<DefaultColorState> = this.lowmainslockDefaultColor.asObservable();

  readonly dipswitchDefaultColor = new BehaviorSubject<DefaultColorState>("");
  readonly getDipswitchDefaultColor: Observable<DefaultColorState> = this.dipswitchDefaultColor.asObservable();

  // --- Default value streams ----------------------------------------
  readonly aloDefaultValue = new BehaviorSubject<DefaultValueState>("");
  readonly getAloDefaultValue: Observable<DefaultValueState> = this.aloDefaultValue.asObservable();

  readonly startupDefaultValue = new BehaviorSubject<DefaultValueState>("");
  readonly getStartupDefaultValue: Observable<DefaultValueState> = this.startupDefaultValue.asObservable();

  readonly aocDefaultValue = new BehaviorSubject<DefaultValueState>("");
  readonly getAocDefaultValue: Observable<DefaultValueState> = this.aocDefaultValue.asObservable();

  readonly srpsuDefaultValue = new BehaviorSubject<DefaultValueState>("");
  readonly getSrPsuDefaultValue: Observable<DefaultValueState> = this.srpsuDefaultValue.asObservable();

  readonly diia253maintenanceDefaultValue = new BehaviorSubject<DefaultValueState>("");
  readonly getdiia253maintenanceDefaultValue: Observable<DefaultValueState> =
    this.diia253maintenanceDefaultValue.asObservable();

  readonly cloDefaultValue = new BehaviorSubject<DefaultValueState>("");
  readonly getCloDefaultValue: Observable<DefaultValueState> = this.cloDefaultValue.asObservable();

  readonly cloLiteDefaultValue = new BehaviorSubject<DefaultValueState>("");
  readonly getCloLiteDefaultValue: Observable<DefaultValueState> = this.cloLiteDefaultValue.asObservable();

  readonly dynaDefaultValue = new BehaviorSubject<DefaultValueState>("");
  readonly getDynaDefaultValue: Observable<DefaultValueState> = this.dynaDefaultValue.asObservable();

  readonly ampdimDefaultValue = new BehaviorSubject<DefaultValueState>("");
  readonly getAmpDimDefaultValue: Observable<DefaultValueState> = this.ampdimDefaultValue.asObservable();

  readonly luminDefaultValue = new BehaviorSubject<DefaultValueState>("");
  readonly getLuminDefaultValue: Observable<DefaultValueState> = this.luminDefaultValue.asObservable();

  readonly lineSwitchDefaultValue = new BehaviorSubject<DefaultValueState>("");
  readonly getLineSwitchDefaultValue: Observable<DefaultValueState> =
    this.lineSwitchDefaultValue.asObservable();

  /** Subject (no current value) for "restore OWP defaults" cross-component pings. */
  readonly owpRestoreDefaultValue = new Subject<DefaultColorState>();
  readonly getowpRestoreDefaultValue: Observable<DefaultColorState> =
    this.owpRestoreDefaultValue.asObservable();

  readonly codedmainsDefaultValue = new BehaviorSubject<DefaultValueState>("");
  readonly getcodedmainsDefaultValue: Observable<DefaultValueState> =
    this.codedmainsDefaultValue.asObservable();

  readonly mtpDefaultValue = new BehaviorSubject<DefaultValueState>("");
  readonly getmtpDefaultValue: Observable<DefaultValueState> = this.mtpDefaultValue.asObservable();

  readonly ztvoutputDefaultValue = new BehaviorSubject<DefaultValueState>("");
  readonly getztvoutputDefaultValue: Observable<DefaultValueState> =
    this.ztvoutputDefaultValue.asObservable();

  readonly lipDefaultValue = new BehaviorSubject<DefaultValueState>("");
  readonly getlipDefaultValue: Observable<DefaultValueState> = this.lipDefaultValue.asObservable();

  readonly rsoDefaultValue = new BehaviorSubject<DefaultValueState>("");
  readonly getrsoDefaultValue: Observable<DefaultValueState> = this.rsoDefaultValue.asObservable();

  readonly dali102AddressingDefaultValue = new BehaviorSubject<DefaultValueState>("");
  readonly getDali102AddressingDefaultValue: Observable<DefaultValueState> =
    this.dali102AddressingDefaultValue.asObservable();

  readonly dali102and207ConfigurationDefaultValue = new BehaviorSubject<DefaultValueState>("");
  readonly getDali102and207ConfigurationDefaultValue: Observable<DefaultValueState> =
    this.dali102and207ConfigurationDefaultValue.asObservable();

  readonly auxDefaultValue = new BehaviorSubject<DefaultValueState>("");
  readonly getAuxDefaultValue: Observable<DefaultValueState> = this.auxDefaultValue.asObservable();

  readonly lsiDefaultValue = new BehaviorSubject<DefaultValueState>("");
  readonly getLsiDefaultValue: Observable<DefaultValueState> = this.lsiDefaultValue.asObservable();

  readonly aocmultichannelDefaultValue = new BehaviorSubject<DefaultValueState>("");
  readonly getAocMultiChannelDefaultValue: Observable<DefaultValueState> =
    this.aocmultichannelDefaultValue.asObservable();

  readonly otpDefaultValue = new BehaviorSubject<DefaultValueState>("");
  readonly getOtpDefaultValue: Observable<DefaultValueState> = this.otpDefaultValue.asObservable();

  readonly dscDefaultValue = new BehaviorSubject<DefaultValueState>("");
  readonly getDscDefaultValue: Observable<DefaultValueState> = this.dscDefaultValue.asObservable();

  readonly lowmainslockDefaultValue = new BehaviorSubject<DefaultValueState>("");
  readonly getLowMainsLockDefaultValue: Observable<DefaultValueState> = this.lowmainslockDefaultValue.asObservable();

  readonly dipswitchDefaultValue = new BehaviorSubject<DefaultValueState>("");
  readonly getDipswitchDefaultValue: Observable<DefaultValueState> = this.dipswitchDefaultValue.asObservable();

  // --- "Default applied" flag streams -------------------------------
  // Note: although seeded with `false`, some callers (e.g. corridor.component)
  // push strings, so we keep them permissive to match the legacy contract.
  readonly setMtpDefault = new BehaviorSubject<DefaultColorState>(false);
  readonly setztvoutputDefault = new BehaviorSubject<DefaultColorState>(false);
  readonly setlipDefault = new BehaviorSubject<DefaultColorState>(false);
  readonly setrsoDefault = new BehaviorSubject<DefaultColorState>(false);
  readonly setCorridorDefault = new BehaviorSubject<DefaultColorState>(false);
  readonly getCorridorDefault: Observable<DefaultColorState> = this.setCorridorDefault.asObservable();
  readonly setLineSwitchDefault = new BehaviorSubject<DefaultColorState>(false);
  readonly setcodedmainsDefault = new BehaviorSubject<DefaultColorState>(false);
  readonly getcodedmainsDefault: Observable<DefaultColorState> = this.setcodedmainsDefault.asObservable();
  readonly setAmpDimDefault = new BehaviorSubject<DefaultColorState>(false);

  readonly setDali102AddressingDefault = new BehaviorSubject<DefaultColorState>(false);
  readonly getDali102AddressingDefault: Observable<DefaultColorState> =
    this.setDali102AddressingDefault.asObservable();

  readonly setDali102and207ConfigurationDefault = new BehaviorSubject<DefaultColorState>(false);
  readonly getDali102and207ConfigurationDefault: Observable<DefaultColorState> =
    this.setDali102and207ConfigurationDefault.asObservable();

  readonly setAuxDefault = new BehaviorSubject<DefaultColorState>(false);
  readonly getAuxDefault: Observable<DefaultColorState> = this.setAuxDefault.asObservable();

  readonly setLsiDefault = new BehaviorSubject<DefaultColorState>(false);
  readonly getLsiDefault: Observable<DefaultColorState> = this.setLsiDefault.asObservable();

  readonly setAocMultiChannelDefault = new BehaviorSubject<DefaultColorState>(false);
  readonly getAocMultiChannelDefault: Observable<DefaultColorState> =
    this.setAocMultiChannelDefault.asObservable();

  readonly setOtpDefault = new BehaviorSubject<DefaultColorState>(false);
  readonly getOtpDefault: Observable<DefaultColorState> = this.setOtpDefault.asObservable();

  readonly setDscDefault = new BehaviorSubject<DefaultColorState>(false);
  readonly getDscDefault: Observable<DefaultColorState> = this.setDscDefault.asObservable();

  readonly setLowMainsLockDefault = new BehaviorSubject<DefaultColorState>(false);
  readonly getLowMainsLockDefault: Observable<DefaultColorState> = this.setLowMainsLockDefault.asObservable();

  readonly setDipswitchDefault = new BehaviorSubject<DefaultColorState>(false);
  readonly getDipswitchDefault: Observable<DefaultColorState> = this.setDipswitchDefault.asObservable();

  /** Generic "restore flextune default" cross-component ping. */
  readonly restoreFlextuneDefault = new Subject<DefaultColorState>();

  // --- Default-color setters ----------------------------------------
  setAloDefaultColor(value: DefaultColorState): void {
    this.aloDefaultColor.next(value);
  }
  setStartupDefaultColor(value: DefaultColorState): void {
    this.startupDefaultColor.next(value);
  }
  setAocDefaultColor(value: DefaultColorState): void {
    this.aocDefaultColor.next(value);
  }
  setSrpsuDefaultColor(value: DefaultColorState): void {
    this.srpsuDefaultColor.next(value);
  }
  setCloDefaultColor(value: DefaultColorState): void {
    this.cloDefaultColor.next(value);
  }
  setCloLiteDefaultColor(value: DefaultColorState): void {
    this.cloLiteDefaultColor.next(value);
  }
  setDynaDefaultColor(value: DefaultColorState): void {
    this.dynaDefaultColor.next(value);
  }
  setAmpDimDefaultColor(value: DefaultColorState): void {
    this.ampdimDefaultColor.next(value);
  }
  setLuminDefaultColor(value: DefaultColorState): void {
    this.luminDefaultColor.next(value);
  }
  setdiia253maintenanceDefaultColor(value: DefaultColorState): void {
    this.diia253maintenanceDefaultColor.next(value);
  }
  setowpDefaultColor(value: DefaultColorState): void {
    this.owpDefaultColor.next(value);
  }
  setTouchDimDefaultColor(value: DefaultColorState): void {
    this.touchDimDefaultColor.next(value);
  }
  setFlextuneDefaultColor(value: DefaultColorState): void {
    this.FlextuneDefaultColor.next(value);
  }
  setDtlDefaultColor(value: DefaultColorState): void {
    this.DtlDefaultColor.next(value);
  }
  setMinDimLevelDefaultColor(value: DefaultColorState): void {
    this.minDimLevelDefaultColor.next(value);
  }
  setDcemergencyDefaultColor(value: DefaultColorState): void {
    this.dcemergencyDefaultColor.next(value);
  }

  // --- Default-value setters ----------------------------------------
  setAloDefaultValue(value: DefaultValueState): void {
    this.aloDefaultValue.next(value);
  }
  setStartupDefaultValue(value: DefaultValueState): void {
    this.startupDefaultValue.next(value);
  }
  setAocDefaultValue(value: DefaultValueState): void {
    this.aocDefaultValue.next(value);
  }
  setSrpsuDefaultValue(value: DefaultValueState): void {
    this.srpsuDefaultValue.next(value);
  }
  setdiia253maintenanceDefaultValue(value: DefaultValueState): void {
    this.diia253maintenanceDefaultValue.next(value);
  }
  setCloDefaultValue(value: DefaultValueState): void {
    this.cloDefaultValue.next(value);
  }
  setCloLiteDefaultValue(value: DefaultValueState): void {
    this.cloLiteDefaultValue.next(value);
  }
  setDynaDefaultValue(value: DefaultValueState): void {
    this.dynaDefaultValue.next(value);
  }
  setAmpDimDefaultValue(value: DefaultValueState): void {
    this.ampdimDefaultValue.next(value);
  }
  setLuminDefaultValue(value: DefaultValueState): void {
    this.luminDefaultValue.next(value);
  }
  setLineSwitchDefaultValue(value: DefaultValueState): void {
    this.lineSwitchDefaultValue.next(value);
  }
  setOwpRestoreDefaultValue(value: DefaultColorState): void {
    this.owpRestoreDefaultValue.next(value);
  }
  setcodedmainsDefaultValue(value: DefaultValueState): void {
    this.codedmainsDefaultValue.next(value);
  }
  setMtpDefaultValue(value: DefaultValueState): void {
    this.mtpDefaultValue.next(value);
  }
  setztvoutputDefaultValue(value: DefaultValueState): void {
    this.ztvoutputDefaultValue.next(value);
  }
  setlipDefaultValue(value: DefaultValueState): void {
    this.lipDefaultValue.next(value);
  }
  setrsoDefaultValue(value: DefaultValueState): void {
    this.rsoDefaultValue.next(value);
  }
  setDali102AddressingDefaultValue(value: DefaultValueState): void {
    this.dali102AddressingDefaultValue.next(value);
  }
  setDali102and207ConfigurationDefaultValue(value: DefaultValueState): void {
    this.dali102and207ConfigurationDefaultValue.next(value);
  }
  setAuxDefaultValue(value: DefaultValueState): void {
    this.auxDefaultValue.next(value);
  }
  setLsiDefaultValue(value: DefaultValueState): void {
    this.lsiDefaultValue.next(value);
  }
  setAocMultiChannelDefaultValue(value: DefaultValueState): void {
    this.aocmultichannelDefaultValue.next(value);
  }

  // --- "Default applied" flag setters -------------------------------
  setMtpDefaultColor(data: DefaultColorState): void {
    this.setMtpDefault.next(data);
  }
  setztvoutputDefaultColor(data: DefaultColorState): void {
    this.setztvoutputDefault.next(data);
  }
  setlipDefaultColor(data: DefaultColorState): void {
    this.setlipDefault.next(data);
  }
  setrsoDefaultColor(data: DefaultColorState): void {
    this.setrsoDefault.next(data);
  }
  setCorridorDefaultColor(data: DefaultColorState): void {
    this.setCorridorDefault.next(data);
  }
  setLineSwitchDefaultColor(data: DefaultColorState): void {
    this.setLineSwitchDefault.next(data);
  }
  setcodedmainsDefaultColor(data: DefaultColorState): void {
    this.setcodedmainsDefault.next(data);
  }
  setDali102AddressingDefaultColor(data: DefaultColorState): void {
    this.setDali102AddressingDefault.next(data);
  }
  setDali102and207ConfigurationDefaultColor(data: DefaultColorState): void {
    this.setDali102and207ConfigurationDefault.next(data);
  }
  setAuxDefaultColor(data: DefaultColorState): void {
    this.setAuxDefault.next(data);
  }
  setLsiDefaultColor(data: DefaultColorState): void {
    this.setLsiDefault.next(data);
  }
  setAocMultiChannelDefaultColor(data: DefaultColorState): void {
    this.setAocMultiChannelDefault.next(data);
  }
  setOtpDefaultColor(data: DefaultColorState): void {
    this.setOtpDefault.next(data);
  }
  setOtpDefaultValue(value: DefaultValueState): void {
    this.otpDefaultValue.next(value);
  }
  setDscDefaultColor(data: DefaultColorState): void {
    this.setDscDefault.next(data);
  }
  setDscDefaultValue(value: DefaultValueState): void {
    this.dscDefaultValue.next(value);
  }
  setLowMainsLockDefaultColor(data: DefaultColorState): void {
    this.setLowMainsLockDefault.next(data);
  }
  setLowMainsLockDefaultValue(value: DefaultValueState): void {
    this.lowmainslockDefaultValue.next(value);
  }
  setDipswitchDefaultColor(data: DefaultColorState): void {
    this.setDipswitchDefault.next(data);
  }
  setDipswitchDefaultValue(value: DefaultValueState): void {
    this.dipswitchDefaultValue.next(value);
  }
}
